import { useGetIdentity, useGetList } from "ra-core";
import { useMemo } from "react";

import type { TeamTask } from "@/components/fretum/types";

import { useAgendaEntries } from "../../mobile/useAgendaEntries";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Deal, Task } from "../../types";
import {
  computeMonthGoal,
  monthBounds,
  summarizeMyTasks,
  type MonthGoal,
  type MyTasksSummary,
} from "./shellModel";

const EMPTY_SUMMARY: MyTasksSummary = { overdue: 0, dueToday: 0, items: [] };

/**
 * Open contact tasks (sales_id = me, not done) and team tasks assigned to me
 * (status other than done), summarized by due day. Shared by the sidebar badge,
 * the greeting subtitle and the bell popover (react-query dedupes the calls).
 */
export const useMyTasks = (): MyTasksSummary & { isPending: boolean } => {
  const { identity } = useGetIdentity();
  const enabled = identity?.id != null;
  const tasks = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
      filter: { sales_id: identity?.id, "done_date@is": null },
    },
    { enabled },
  );
  const teamTasks = useGetList<TeamTask>(
    "team_tasks",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
      filter: { assignee_id: identity?.id, "status@neq": "done" },
    },
    { enabled, retry: false },
  );
  const summary = useMemo(
    () =>
      tasks.data || teamTasks.data
        ? summarizeMyTasks(tasks.data ?? [], teamTasks.data ?? [], new Date())
        : EMPTY_SUMMARY,
    [tasks.data, teamTasks.data],
  );
  return { ...summary, isPending: tasks.isPending || teamTasks.isPending };
};

/** Today's agenda entries (same sources as /agenda) that are not done. */
export const useTodayAgendaCount = (): number => {
  const today = useMemo(() => new Date(), []);
  const { entries } = useAgendaEntries(today, today);
  return entries.filter((entry) => !entry.done).length;
};

/** Monthly goal of the sidebar status card, from the deals closing this month. */
export const useMonthGoal = (): { goal: MonthGoal; isPending: boolean } => {
  const { dealPipelineStatuses } = useConfigurationContext();
  const bounds = useMemo(() => monthBounds(new Date()), []);
  const { data, isPending } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "expected_closing_date", order: "ASC" },
    filter: {
      "archived_at@is": null,
      "expected_closing_date@gte": bounds.start.toISOString(),
      "expected_closing_date@lt": bounds.end.toISOString(),
    },
  });
  const goal = useMemo(
    () => computeMonthGoal(data ?? [], dealPipelineStatuses, new Date()),
    [data, dealPipelineStatuses],
  );
  return { goal, isPending };
};
